import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { AlertCircle, Copy, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddPatientModal = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [tempPassword, setTempPassword] = useState('');
  const [createdEmail, setCreatedEmail] = useState('');
  const [employers, setEmployers] = useState([]);
  
  const [formData, setFormData] = useState({
    email: '',
    mobile_number: '',
    first_name: '',
    middle_name: '',
    last_name: '',
    nickname: '',
    birthdate: '',
    sex: '',
    pronouns: '',
    birthplace: '',
    location: '',
    home_address: '',
    occupation: '',
    employer_name: '',
    work_address: '',
    emergency_contact_name: '',
    emergency_contact_relationship: '',
    emergency_contact_phone: '',
    membership_start_date: '',
    membership_expiry_date: '',
    employer_id: ''
  });

  React.useEffect(() => {
    if (open) {
      fetchEmployers();
    }
  }, [open]);

  const fetchEmployers = async () => {
    try {
      const response = await axios.get(`${API}/employers`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setEmployers(response.data);
    } catch (error) {
      console.error('Error fetching employers:', error);
    }
  };

  // NCR Cities (16 cities within Metro Manila)
  const ncrCities = [
    'Manila', 'Quezon City', 'Makati', 'Pasig', 'Taguig', 'Parañaque', 
    'Muntinlupa', 'Las Piñas', 'Mandaluyong', 'Marikina', 'Pasay', 
    'Caloocan', 'Malabon', 'Navotas', 'Valenzuela', 'San Juan'
  ];

  // All other provinces (excluding Metro Manila since we list NCR cities individually)
  const provinces = [
    'Abra', 'Agusan del Norte', 'Agusan del Sur', 'Aklan', 'Albay', 'Antique',
    'Apayao', 'Aurora', 'Basilan', 'Bataan', 'Batanes', 'Batangas', 'Benguet', 'Biliran',
    'Bohol', 'Bukidnon', 'Bulacan', 'Cagayan', 'Camarines Norte', 'Camarines Sur', 'Camiguin',
    'Capiz', 'Catanduanes', 'Cavite', 'Cebu', 'Cotabato', 'Davao de Oro', 'Davao del Norte',
    'Davao del Sur', 'Davao Occidental', 'Davao Oriental', 'Dinagat Islands', 'Eastern Samar',
    'Guimaras', 'Ifugao', 'Ilocos Norte', 'Ilocos Sur', 'Iloilo', 'Isabela', 'Kalinga',
    'La Union', 'Laguna', 'Lanao del Norte', 'Lanao del Sur', 'Leyte', 'Maguindanao',
    'Marinduque', 'Masbate', 'Misamis Occidental', 'Misamis Oriental', 'Mountain Province',
    'Negros Occidental', 'Negros Oriental', 'Northern Samar', 'Nueva Ecija', 'Nueva Vizcaya',
    'Occidental Mindoro', 'Oriental Mindoro', 'Palawan', 'Pampanga', 'Pangasinan', 'Quezon',
    'Quirino', 'Rizal', 'Romblon', 'Samar', 'Sarangani', 'Siquijor', 'Sorsogon',
    'South Cotabato', 'Southern Leyte', 'Sultan Kudarat', 'Sulu', 'Surigao del Norte',
    'Surigao del Sur', 'Tarlac', 'Tawi-Tawi', 'Zambales', 'Zamboanga del Norte',
    'Zamboanga del Sur', 'Zamboanga Sibugay'
  ];

  // Combine NCR cities and provinces for the location dropdown
  const locationOptions = [
    { label: '--- NCR Cities ---', value: '', disabled: true },
    ...ncrCities.map(city => ({ label: city, value: city })),
    { label: '--- Provinces ---', value: '', disabled: true },
    ...provinces.map(province => ({ label: province, value: province }))
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validatePhoneNumber = (phone) => {
    // Philippine mobile number format: +639XXXXXXXXX or 09XXXXXXXXX
    const pattern = /^(\+639|09)\d{9}$/;
    return pattern.test(phone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate mobile number
    if (!validatePhoneNumber(formData.mobile_number)) {
      setError('Invalid mobile number format. Please use +639XXXXXXXXX or 09XXXXXXXXX format.');
      return;
    }

    // Validate emergency contact phone only if provided
    if (formData.emergency_contact_phone && !validatePhoneNumber(formData.emergency_contact_phone)) {
      setError('Invalid emergency contact phone format. Please use +639XXXXXXXXX or 09XXXXXXXXX format.');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/add-patient`, formData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Store the temporary password and email to show in modal
      setTempPassword(response.data.temporary_password);
      setCreatedEmail(formData.email);
      setShowPasswordModal(true);
      
      onSuccess();
      onClose();
      setFormData({
        email: '',
        mobile_number: '',
        first_name: '',
        middle_name: '',
        last_name: '',
        nickname: '',
        birthdate: '',
        sex: '',
        pronouns: '',
        birthplace: '',
        location: '',
        home_address: '',
        occupation: '',
        employer_name: '',
        work_address: '',
        emergency_contact_name: '',
        emergency_contact_relationship: '',
        emergency_contact_phone: '',
        membership_start_date: '',
        membership_expiry_date: '',
        employer_id: ''
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred while adding patient');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Patient</DialogTitle>
        </DialogHeader>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="col-span-2">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Account Information</h3>
            </div>

            <div>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="mt-1"
                data-testid="add-patient-email"
              />
            </div>

            <div>
              <Label htmlFor="mobile_number">Mobile Number *</Label>
              <Input
                id="mobile_number"
                name="mobile_number"
                type="tel"
                value={formData.mobile_number}
                onChange={handleChange}
                required
                pattern="(\+639|09)\d{9}"
                title="Philippine mobile number: +639XXXXXXXXX or 09XXXXXXXXX"
                className="mt-1"
                placeholder="+639171234567 or 09171234567"
                data-testid="add-patient-mobile"
              />
              <p className="text-xs text-slate-500 mt-1">Format: +639XXXXXXXXX or 09XXXXXXXXX</p>
            </div>

            <div className="col-span-2">
              <p className="text-xs text-slate-500">A temporary password will be generated and shown after creation</p>
            </div>

            <div className="col-span-2">
              <h3 className="text-sm font-semibold text-slate-700 mb-1 mt-4">Personal Information</h3>
              <p className="text-xs text-slate-500 mb-3">Only name fields are required. The patient can complete the rest from their dashboard.</p>
            </div>

            <div>
              <Label htmlFor="first_name">First Name *</Label>
              <Input
                id="first_name"
                name="first_name"
                type="text"
                value={formData.first_name}
                onChange={handleChange}
                required
                className="mt-1"
                data-testid="add-patient-first-name"
              />
            </div>

            <div>
              <Label htmlFor="middle_name">Middle Name</Label>
              <Input
                id="middle_name"
                name="middle_name"
                type="text"
                value={formData.middle_name}
                onChange={handleChange}
                className="mt-1"
                data-testid="add-patient-middle-name"
              />
            </div>

            <div>
              <Label htmlFor="last_name">Last Name *</Label>
              <Input
                id="last_name"
                name="last_name"
                type="text"
                value={formData.last_name}
                onChange={handleChange}
                required
                className="mt-1"
                data-testid="add-patient-last-name"
              />
            </div>

            <div>
              <Label htmlFor="nickname">Nickname</Label>
              <Input
                id="nickname"
                name="nickname"
                type="text"
                value={formData.nickname}
                onChange={handleChange}
                className="mt-1"
                data-testid="add-patient-nickname"
              />
            </div>

            <div>
              <Label htmlFor="birthdate">Birthdate</Label>
              <Input
                id="birthdate"
                name="birthdate"
                type="date"
                value={formData.birthdate}
                onChange={handleChange}
                max={new Date().toISOString().split('T')[0]}
                className="mt-1"
                data-testid="add-patient-birthdate"
              />
            </div>

            <div>
              <Label htmlFor="birthplace">Birthplace</Label>
              <Input
                id="birthplace"
                name="birthplace"
                type="text"
                value={formData.birthplace}
                onChange={handleChange}
                className="mt-1"
                data-testid="add-patient-birthplace"
              />
            </div>

            <div>
              <Label htmlFor="pronouns">Pronouns</Label>
              <select
                id="pronouns"
                name="pronouns"
                value={formData.pronouns}
                onChange={handleChange}
                className="mt-1 w-full rounded-lg border-slate-200 px-4 py-2.5"
                data-testid="add-patient-pronouns"
              >
                <option value="">Select...</option>
                <option value="he/him">he/him</option>
                <option value="she/her">she/her</option>
                <option value="they/them">they/them</option>
              </select>
            </div>

            <div>
              <Label htmlFor="sex">Sex Assigned at Birth</Label>
              <select
                id="sex"
                name="sex"
                value={formData.sex}
                onChange={handleChange}
                className="mt-1 w-full rounded-lg border-slate-200 px-4 py-2.5"
                data-testid="add-patient-sex"
              >
                <option value="">Select...</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Intersex">Intersex</option>
              </select>
            </div>

            <div>
              <Label htmlFor="location">Location (NCR City or Province)</Label>
              <select
                id="location"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="mt-1 w-full rounded-lg border-slate-200 px-4 py-2.5"
                data-testid="add-patient-location"
              >
                <option value="">Select Location...</option>
                {locationOptions.map((option, index) => (
                  <option 
                    key={option.value || `loc-${index}`} 
                    value={option.value} 
                    disabled={option.disabled}
                    className={option.disabled ? 'font-semibold text-slate-400 bg-slate-100' : ''}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label htmlFor="home_address">Home Address</Label>
              <Input
                id="home_address"
                name="home_address"
                type="text"
                value={formData.home_address}
                onChange={handleChange}
                className="mt-1"
                data-testid="add-patient-home-address"
              />
            </div>

            <div>
              <Label htmlFor="occupation">Occupation</Label>
              <Input
                id="occupation"
                name="occupation"
                type="text"
                value={formData.occupation}
                onChange={handleChange}
                className="mt-1"
                data-testid="add-patient-occupation"
              />
            </div>

            <div>
              <Label htmlFor="employer_name">Employer</Label>
              <Input
                id="employer_name"
                name="employer_name"
                type="text"
                value={formData.employer_name}
                onChange={handleChange}
                className="mt-1"
                data-testid="add-patient-employer"
              />
            </div>

            <div className="col-span-2">
              <Label htmlFor="work_address">Work Address</Label>
              <Input
                id="work_address"
                name="work_address"
                type="text"
                value={formData.work_address}
                onChange={handleChange}
                className="mt-1"
                data-testid="add-patient-work-address"
              />
            </div>

            <div className="col-span-2">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 mt-4">Emergency Contact</h3>
            </div>

            <div>
              <Label htmlFor="emergency_contact_name">Name</Label>
              <Input
                id="emergency_contact_name"
                name="emergency_contact_name"
                type="text"
                value={formData.emergency_contact_name}
                onChange={handleChange}
                className="mt-1"
                data-testid="add-patient-ec-name"
              />
            </div>

            <div>
              <Label htmlFor="emergency_contact_relationship">Relationship</Label>
              <Input
                id="emergency_contact_relationship"
                name="emergency_contact_relationship"
                type="text"
                value={formData.emergency_contact_relationship}
                onChange={handleChange}
                className="mt-1"
                data-testid="add-patient-ec-relationship"
              />
            </div>

            <div>
              <Label htmlFor="emergency_contact_phone">Phone</Label>
              <Input
                id="emergency_contact_phone"
                name="emergency_contact_phone"
                type="tel"
                value={formData.emergency_contact_phone}
                onChange={handleChange}
                className="mt-1"
                placeholder="+639171234567 or 09171234567"
                data-testid="add-patient-ec-phone"
              />
            </div>

            <div className="col-span-2">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 mt-4">Membership Information</h3>
            </div>

            <div>
              <Label htmlFor="membership_start_date">Start Date *</Label>
              <Input
                id="membership_start_date"
                name="membership_start_date"
                type="date"
                value={formData.membership_start_date}
                onChange={handleChange}
                required
                max="9999-12-31"
                className="mt-1"
                data-testid="add-patient-membership-start"
              />
            </div>

            <div>
              <Label htmlFor="membership_expiry_date">Expiry Date *</Label>
              <Input
                id="membership_expiry_date"
                name="membership_expiry_date"
                type="date"
                value={formData.membership_expiry_date}
                onChange={handleChange}
                required
                max="9999-12-31"
                className="mt-1"
                data-testid="add-patient-membership-expiry"
              />
            </div>
          </div>

          {/* Employer Assignment (Optional) */}
          {employers.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="col-span-2">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 mt-4">Employer Assignment</h3>
              </div>
              <div className="col-span-2">
                <Label htmlFor="employer_id">Assign to Employer</Label>
                <select
                  id="employer_id"
                  name="employer_id"
                  value={formData.employer_id}
                  onChange={handleChange}
                  className="mt-1 w-full rounded-lg border-slate-200 px-4 py-2.5"
                  data-testid="add-patient-employer-id"
                >
                  <option value="">No employer (individual member)</option>
                  {employers.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.company_name || `${emp.first_name} ${emp.last_name}`}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">Optional. You can assign an employer later from the patient edit page.</p>
              </div>
            </div>
          )}

          <div className="flex gap-4 pt-4">
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 btn-primary"
            >
              {loading ? 'Adding Patient...' : 'Add Patient'}
            </Button>
            <Button
              type="button"
              onClick={onClose}
              className="flex-1 btn-secondary"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>

      {/* Temporary Password Display Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-green-700 flex items-center gap-2">
              <CheckCircle size={24} />
              Patient Account Created!
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <Alert className="bg-blue-50 border-blue-200">
              <AlertCircle className="h-4 w-4 text-blue-600" />
              <AlertDescription className="text-blue-900">
                <strong>Important:</strong> Share these credentials with the patient. They should change their password immediately after first login.
              </AlertDescription>
            </Alert>

            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-3">
              <div>
                <Label className="text-xs text-slate-600 uppercase font-semibold">Email</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input value={createdEmail} readOnly className="font-mono text-sm" />
                  <Button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(createdEmail);
                      toast.success('Email copied!');
                    }}
                    className="bg-slate-600 hover:bg-slate-700 text-white px-3 py-2"
                  >
                    <Copy size={16} />
                  </Button>
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-600 uppercase font-semibold">Temporary Password</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input value={tempPassword} readOnly className="font-mono text-sm bg-yellow-50 border-yellow-300" />
                  <Button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(tempPassword);
                      toast.success('Password copied!');
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2"
                  >
                    <Copy size={16} />
                  </Button>
                </div>
              </div>
            </div>

            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-900">
                <strong>Email Sent!</strong> The patient should receive their login credentials at the email address provided. Ask them to check their spam folder if needed.
              </AlertDescription>
            </Alert>

            <Button
              onClick={() => {
                setShowPasswordModal(false);
                setTempPassword('');
                setCreatedEmail('');
              }}
              className="w-full btn-primary"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default AddPatientModal;